<template>
  <div>
    <h1 class="text-center mb-8">Who's imaging tonight?</h1>

    <section class="mb-2">
      <StarChart :sessions="sessions" :moon="moon" />
      <div class="text-right text-xs mt-2 text-gray-300 italic">
        <template v-if="activeLookback === 'all'">Showing all sessions</template>
        <template v-else>Showing sessions from the past {{ activeLookback }} hours</template>
      </div>
    </section>

    <div v-if="loading" class="text-center py-8 text-gray-300">Loading...</div>

    <div v-else-if="error" class="text-center py-8 text-red-400">{{ error }}</div>

    <template v-else>
      <section>
        <div class="masonry col-1 sm:col-2 md:col-4 before:box-inherit after:box-inherit">
          <SessionCard
            v-for="session in visibleSessions"
            :key="session.id"
            :session="session"
          />
        </div>
      </section>

      <nav v-if="totalPages > 1" class="mt-8 flex flex-col items-center gap-3">
        <div class="flex items-center gap-2 text-sm">
          <button
            class="px-4 py-2 rounded-full border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            :disabled="currentPage === 1"
            @click="currentPage--"
          >
            ← Prev
          </button>

          <template v-for="item in pageItems" :key="item">
            <span v-if="item === '...'" class="w-9 text-center text-gray-600 select-none">…</span>
            <button
              v-else
              :class="[
                'w-9 h-9 rounded-full text-sm font-medium transition-colors',
                item === currentPage
                  ? 'bg-yellow-300 text-gray-900'
                  : 'border border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white'
              ]"
              @click="currentPage = item as number"
            >
              {{ item }}
            </button>
          </template>

          <button
            class="px-4 py-2 rounded-full border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            :disabled="currentPage === totalPages"
            @click="currentPage++"
          >
            Next →
          </button>
        </div>

        <p class="text-xs text-gray-500">
          Showing {{ pageStart }}–{{ pageEnd }} of {{ sessions.length }}
        </p>
      </nav>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import SessionCard from '../components/SessionCard.vue'
import StarChart from '../components/StarChart.vue'

const PAGE_SIZE = 24

const route = useRoute()

const sessions = ref<any[]>([])
const moon = ref<{ ra: number; dec: number } | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const currentPage = ref(1)

const activeLookback = computed(() => (route.query.lookback as string) || '18')
const totalPages = computed(() => Math.ceil(sessions.value.length / PAGE_SIZE))
const pageStart = computed(() => (currentPage.value - 1) * PAGE_SIZE + 1)
const pageEnd = computed(() => Math.min(currentPage.value * PAGE_SIZE, sessions.value.length))
const visibleSessions = computed(() =>
  sessions.value.slice(pageStart.value - 1, pageEnd.value)
)

const pageItems = computed((): (number | '...')[] => {
  const total = totalPages.value
  const cur = currentPage.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const items: (number | '...')[] = [1]
  if (cur > 3) items.push('...')
  for (let p = Math.max(2, cur - 1); p <= Math.min(total - 1, cur + 1); p++) items.push(p)
  if (cur < total - 2) items.push('...')
  items.push(total)
  return items
})

async function fetchSessions() {
  loading.value = true
  error.value = null
  currentPage.value = 1

  const params = new URLSearchParams()
  if (route.query.lookback) params.set('lookback', route.query.lookback as string)
  if (route.query.date) params.set('date', route.query.date as string)

  try {
    const response = await fetch(`/api/spa/sessions?${params}`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
    if (!response.ok) throw new Error(`Request failed: ${response.status}`)
    const data = await response.json()
    sessions.value = data.sessions
    moon.value = data.moon ?? null
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load sessions'
  } finally {
    loading.value = false
  }
}

watch(() => route.query, fetchSessions, { deep: true })
onMounted(fetchSessions)
</script>
